import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdOutlineTimer, MdOutlineQuiz, MdAttachFile, MdCalendarToday, MdPlayCircleOutline } from 'react-icons/md';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getURL } from '../utils/api';

/* ─── Styled Components ─────────────────────────────────────────────────── */

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: #9ca3af;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const ExamCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ExamName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #111827;
`;

const ExamDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #374151;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px 10px;
`;

const Divider = styled.div`
  border-top: 1px solid #f3f4f6;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const SharedDate = styled.div`
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AttachmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #3b82f6;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const DownloadLink = styled(AttachmentLink)`
  color: #166534;
`;

const StartButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.88;
  }
  &:active {
    opacity: 0.75;
  }
`;

const NoBatchBanner = styled.div`
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 16px 20px;
  color: #92400e;
  font-size: 14px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px 20px;
  color: #b91c1c;
  font-size: 14px;
`;

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const buildAttachmentHref = (attachmentPath, options = {}) => {
  if (!attachmentPath) return null;
  const raw = String(attachmentPath).trim();
  const lowered = raw.toLowerCase();
  if (!raw || lowered === 'null' || lowered === 'undefined') return null;
  const base = getURL(); // e.g. "http://localhost:3000/api"
  const normalised = raw.startsWith('/api/')
    ? raw.slice(4) // strip leading /api so we don't double it
    : raw;
  const suffix = options?.download ? '?download=1' : '';
  return `${base}${normalised}${suffix}`;
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoString;
  }
};

const toSentenceCase = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/* ─── Component ──────────────────────────────────────────────────────────── */

export default function MyExam() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const courseInfo = AuthService.getCourseInfo();
  const batchId = courseInfo?.batchId ?? null;
  const batchName = courseInfo?.batchName ?? null;

  const fetchExams = useCallback(async () => {
    if (!batchId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.get('/students/my-exams', {
        params: { batchId },
      });
      setExams(data.exams || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load exams. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  /* ─ Render ─ */

  const renderContent = () => {
    if (!batchId) {
      return (
        <NoBatchBanner>
          No active course selected. Please select a course from the dashboard to view your exams.
        </NoBatchBanner>
      );
    }

    if (loading) {
      return <Loader />;
    }

    if (error) {
      return <ErrorBanner>{error}</ErrorBanner>;
    }

    if (exams.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>📋</EmptyIcon>
          <EmptyText>No exams have been shared with your batch yet.</EmptyText>
        </EmptyState>
      );
    }

    return (
      <Grid>
        {exams.map((exam) => {
          const attachmentHref = buildAttachmentHref(exam.attachmentPath);
          const attachmentDownloadHref = buildAttachmentHref(exam.attachmentPath, { download: true });
          const attachmentFileName = `${toSentenceCase(exam.examName || 'exam')}-attachment`;
          return (
            <ExamCard key={exam.shareId}>
              <ExamName>{toSentenceCase(exam.examName)}</ExamName>

              {exam.description && (
                <ExamDescription>{exam.description}</ExamDescription>
              )}

              <MetaRow>
                {exam.minutes > 0 && (
                  <MetaBadge>
                    <MdOutlineTimer size={13} />
                    {exam.minutes} min
                  </MetaBadge>
                )}
                {exam.marksForMcq > 0 && (
                  <MetaBadge>
                    <MdOutlineQuiz size={13} />
                    {exam.marksForMcq} marks
                  </MetaBadge>
                )}
                {exam.mcqCount > 0 && (
                  <MetaBadge>
                    <MdOutlineQuiz size={13} />
                    {exam.mcqCount} {exam.mcqCount === 1 ? 'question' : 'questions'}
                  </MetaBadge>
                )}
              </MetaRow>

              <Divider />

              <CardFooter>
                <SharedDate>
                  <MdCalendarToday size={11} />
                  Shared {formatDate(exam.sharedAt)}
                </SharedDate>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {attachmentHref && (
                    <>
                      <AttachmentLink
                        href={attachmentHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MdAttachFile size={13} />
                        Material
                      </AttachmentLink>
                      <DownloadLink
                        href={attachmentDownloadHref}
                        download={attachmentFileName}
                      >
                        <MdAttachFile size={13} />
                        Download
                      </DownloadLink>
                    </>
                  )}
                  <StartButton
                    onClick={() => {
                      const targetUrl = `/app/my-exam/${exam.examId}/start?batchId=${encodeURIComponent(batchId)}`;
                      window.open(targetUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <MdPlayCircleOutline size={16} />
                    Start Exam
                  </StartButton>
                </div>
              </CardFooter>
            </ExamCard>
          );
        })}
      </Grid>
    );
  };

  return (
    <StudentLayout>
      <Wrapper>
        <PageHeader>
          <Title>My Exams</Title>
          <Subtitle>
            {batchName
              ? `Showing exams shared with ${batchName}`
              : 'Online exams available for your batch'}
          </Subtitle>
        </PageHeader>
        {renderContent()}
      </Wrapper>
    </StudentLayout>
  );
}
